import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loanAccountsApi } from '../api/loanAccounts';
import { loanTransactionsApi } from '../api/loanTransactions';
import type { LoanTransaction, LoanTransactionType, PaymentMode } from '@coreledger/shared-types';
import { FeatureGate } from '../components/FeatureGate';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';
import { LOAN_TYPE_LABELS } from './LoansCapitalPage';

// The per-loan-account "Transactions" detail page (route
// /loans-capital/loans/:id) — see LoansCapitalPage.tsx (the account list
// half) for why this drill-down shape exists, mirroring
// BankingPage.tsx→BankTransactionsPage.tsx. Every transaction here is
// already scoped to this one account, so — unlike Bank Reconciliation's
// LedgerCell, which needs a full LoanAccountSelector — "+ Add Transaction"
// needs no account picker at all.

const fmt = (n: number) => '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const today = () => new Date().toISOString().slice(0, 10);

const PAYMENT_MODES: PaymentMode[] = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];
const MODE_LABELS: Record<string, string> = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

// Same section/rate list ExpensesPage.tsx's own TDS_SECTIONS uses — this
// codebase duplicates this list per page rather than centralizing it (see
// CLAUDE.md's own note on why). 393(1)(ca) (prev. 194A, Interest) is the
// section that actually applies to loan interest — kept first since it's
// the overwhelmingly common case here, but the full list is offered in case
// a real transaction genuinely needs a different one.
const TDS_SECTIONS: { section: string; label: string; rate: number }[] = [
  { section: '393(1)(ca)', label: '393(1)(ca) — Interest [prev. 194A]', rate: 10 },
  { section: '192', label: '192 — Salaries', rate: 0 },
  { section: '193', label: '193 — Interest on Securities', rate: 10 },
  { section: '195', label: '195 — Payments to Non-Residents', rate: 20 },
];

function emptyForm(): Partial<LoanTransaction> {
  return { type: 'disbursement', date: today(), amount: 0, mode: 'bank_transfer' };
}

export default function LoanAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canDelete } = useRole();
  const qc = useQueryClient();
  const [modal, setModal] = useState<null | 'create' | LoanTransaction>(null);
  const [form, setForm] = useState<Partial<LoanTransaction>>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ['loan-account', id],
    queryFn: () => loanAccountsApi.get(id!),
    enabled: !!id,
    staleTime: 10 * 1000,
  });
  const { data: txnData, isLoading: txnLoading } = useQuery({
    queryKey: ['loan-transactions', id],
    queryFn: () => loanTransactionsApi.list(1, 200, { loanAccountId: id }),
    enabled: !!id,
    staleTime: 10 * 1000,
  });
  const transactions = txnData?.data ?? [];

  function refresh() {
    qc.invalidateQueries({ queryKey: ['loan-account', id] });
    qc.invalidateQueries({ queryKey: ['loan-transactions', id] });
    qc.refetchQueries({ type: 'all' });
  }

  async function save() {
    if (!form.amount || form.amount <= 0) { setError('Enter a valid amount'); return; }
    if (!form.date) { setError('Date is required'); return; }
    if (form.type === 'repayment' && form.interestAmount != null && form.interestAmount > form.amount) {
      setError('Interest amount cannot exceed the total amount'); return;
    }
    setError(null); setSaving(true);
    try {
      const dto: Partial<LoanTransaction> = { ...form, loanAccountId: id };
      if (modal && modal !== 'create') await loanTransactionsApi.update(modal.id, dto);
      else await loanTransactionsApi.create(dto);
      refresh();
      setModal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const deleteTxn = useMutation({
    mutationFn: loanTransactionsApi.delete,
    onSuccess: refresh,
  });

  if (accountLoading || !account) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  }

  const principalOf = (t: LoanTransaction) => t.type === 'repayment' ? t.amount - (t.interestAmount ?? 0) : t.amount;

  return (
    <FeatureGate feature="balanceSheet">
    <div className="page">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: '0.75rem' }} onClick={() => navigate('/loans-capital')}>← Back to Loans &amp; Capital</button>

      <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.3rem', fontWeight: 700, margin: '0 0 0.3rem' }}>
              {account.lenderName}
              {account.isRelatedParty && <span className="badge badge-orange" style={{ marginLeft: '0.5rem' }}>Related Party</span>}
            </h1>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.82rem', color: 'var(--text-2)' }}>
              <span>{LOAN_TYPE_LABELS[account.loanType]}</span>
              <span>·</span>
              <span className={`badge ${account.security === 'secured' ? 'badge-blue' : 'badge-gray'}`}>{account.security === 'secured' ? 'Secured' : 'Unsecured'}</span>
              {account.accountNumber && <><span>·</span><span>A/c {account.accountNumber}</span></>}
              {account.interestRate != null && <><span>·</span><span>{account.interestRate}% p.a.</span></>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Outstanding Principal</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{fmt(account.outstandingPrincipal)}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Transactions</h2>
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm()); setModal('create'); }}>+ Add Transaction</button>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr><th>Date</th><th>Type</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Interest</th><th style={{ textAlign: 'right' }}>Principal</th><th>Mode</th><th>Reference</th><th></th></tr>
          </thead>
          <tbody>
            {txnLoading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No transactions yet</td></tr>
            ) : transactions.map(t => (
              <tr key={t.id}>
                <td>{t.date}</td>
                <td><span className={`badge ${t.type === 'disbursement' ? 'badge-green' : 'badge-orange'}`}>{t.type === 'disbursement' ? 'Disbursement' : 'Repayment'}</span></td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(t.amount)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{t.type === 'repayment' && t.interestAmount != null ? fmt(t.interestAmount) : '—'}</td>
                <td style={{ textAlign: 'right' }}>{fmt(principalOf(t))}</td>
                <td>{MODE_LABELS[t.mode]}</td>
                <td style={{ color: 'var(--text-muted)' }}>{t.reference ?? '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setForm(t); setModal(t); }}>Edit</button>
                    {canDelete && <button className="btn btn-danger btn-sm" disabled={deleteTxn.isPending} onClick={async () => { if (await confirm({ message: 'Delete this transaction?', danger: true })) deleteTxn.mutate(t.id); }}>Del</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transaction Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Add Transaction' : 'Edit Transaction'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-box" style={{ marginBottom: '0.75rem' }}>{error}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Type
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as LoanTransactionType, interestAmount: e.target.value === 'disbursement' ? undefined : f.interestAmount }))} style={{ marginTop: '0.3rem', width: '100%' }}>
                    <option value="disbursement">Loan Received (Disbursement)</option>
                    <option value="repayment">Loan Repayment (EMI)</option>
                  </select>
                </label>
                <div className="form-row form-row-2">
                  <label>Amount (₹) *
                    <input type="number" min={0.01} step={0.01} value={form.amount || ''} onChange={e => setForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
                  </label>
                  <label>Date *
                    <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                  </label>
                </div>

                {form.type === 'repayment' && (
                  <>
                    <label>Interest Amount (₹, optional)
                      <input
                        type="number" min={0} step={0.01} value={form.interestAmount ?? ''}
                        onChange={e => setForm(f => ({ ...f, interestAmount: e.target.value ? parseFloat(e.target.value) : undefined }))}
                        placeholder="0.00"
                      />
                    </label>
                    {form.amount != null && form.interestAmount != null && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        Principal portion: <strong>{fmt(Math.max(0, form.amount - form.interestAmount))}</strong>
                      </div>
                    )}
                    {form.interestAmount != null && form.interestAmount > 0 && (
                      <div className="form-row form-row-2">
                        <label>TDS Section (optional)
                          <select
                            value={form.tdsSection ?? ''}
                            onChange={e => {
                              const sec = TDS_SECTIONS.find(s => s.section === e.target.value);
                              setForm(f => ({ ...f, tdsSection: e.target.value || undefined, tdsRate: sec ? sec.rate : f.tdsRate }));
                            }}
                          >
                            <option value="">None</option>
                            {TDS_SECTIONS.map(s => <option key={s.section} value={s.section}>{s.label}</option>)}
                          </select>
                        </label>
                        <label>TDS Rate (%)
                          <input type="number" min={0} step={0.01} value={form.tdsRate ?? ''} onChange={e => setForm(f => ({ ...f, tdsRate: e.target.value ? parseFloat(e.target.value) : undefined }))} />
                        </label>
                      </div>
                    )}
                  </>
                )}

                <div className="form-row form-row-2">
                  <label>Mode
                    <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value as PaymentMode }))}>
                      {PAYMENT_MODES.map(m => <option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                    </select>
                  </label>
                  <label>Reference (optional)
                    <input value={form.reference ?? ''} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Cheque / UTR / TXN #" />
                  </label>
                </div>
                <label>Notes (optional)
                  <input value={form.notes ?? ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </FeatureGate>
  );
}
